import React from 'react';
import moment, { Moment } from 'moment';
import { JobsiteTabProps } from 'containers/jobsite/types';
import {
  Schedule,
  JobsiteSchedulesContextType,
} from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import {
  getDateTimeMoment,
  getJobsiteDateTimeNow,
  getJobsiteMomentToday,
} from 'utils/dates';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import {
  GetScheduleDetailsDocument,
  ScheduleStatus,
  useChangeScheduleStatusMutation,
  useGetScheduleDetailsQuery,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { ApolloError } from '@apollo/client';
import { getGraphQLError } from 'utils/error';
import { JobsiteSchedulesContext } from './JobsiteSchedulesContext';

export function JobsiteSchedulesProvider(
  props: React.PropsWithChildren<JobsiteTabProps>,
): React.ReactElement {
  const {
    children,
    jobsiteId,
    onTabApiChange,
    loading: parentLoading,
    timeZone,
  } = props;
  const { defaultDate } = React.useMemo(() => {
    const { date } = timeZone ? getJobsiteDateTimeNow({ timeZone }) : {};
    return {
      defaultDate: moment(date).startOf('day'),
    };
  }, [timeZone]);

  const [changeScheduleStatus] = useChangeScheduleStatusMutation({
    refetchQueries: [GetScheduleDetailsDocument],
    onCompleted: () => {
      AlertService.alert(
        'success',
        'Success',
        'Schedule status successfully updated',
      );
    },
    onError: (error: ApolloError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
  });

  const {
    date,
    attendanceStatuses,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    defaultValues: {
      date: defaultDate,
      attendanceStatuses: [],
    },
  });

  const attendanceInput = React.useMemo(() => {
    return attendanceStatuses?.length
      ? {
          statuses: attendanceStatuses,
        }
      : undefined;
  }, [attendanceStatuses]);

  const {
    data,
    loading: isDataLoading,
    refetch,
  } = useGetScheduleDetailsQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: isUrlLoading,
    variables: {
      scheduleInput: {
        jobsiteId,
        date: date.format('YYYY-MM-DD'),
      },
      attendanceInput,
    },
  });

  const loading = isDataLoading || isUrlLoading || parentLoading;

  const selectedSchedule: Schedule = React.useMemo(() => {
    return data?.getSchedule;
  }, [data]);

  const selectedScheduleId = selectedSchedule?.id;
  const updateScheduleStatus = React.useCallback(
    async (status: ScheduleStatus) => {
      if (!selectedScheduleId) {
        return;
      }
      await changeScheduleStatus({
        variables: {
          input: {
            id: selectedScheduleId,
            status,
          },
        },
      });
    },
    [selectedScheduleId, changeScheduleStatus],
  );

  const completeSchedule = React.useCallback(async () => {
    await updateScheduleStatus(ScheduleStatus.Completed);
  }, [updateScheduleStatus]);

  const editSchedule = React.useCallback(async () => {
    await updateScheduleStatus(ScheduleStatus.InProgress);
  }, [updateScheduleStatus]);

  const gotoNextDay = React.useCallback((): void => {
    const nextDate = moment(date).add(1, 'day');
    updateUrl({ date: nextDate });
  }, [date, updateUrl]);

  const gotoPrevDay = React.useCallback((): void => {
    const prevDate = moment(date).subtract(1, 'day');
    updateUrl({ date: prevDate });
  }, [date, updateUrl]);

  const handleDateChange = React.useCallback(
    (newDate: Moment): void => updateUrl({ date: newDate }),
    [updateUrl],
  );

  const canChangeSchedule = React.useMemo((): boolean => {
    return (
      selectedSchedule && selectedSchedule.status === ScheduleStatus.InProgress
    );
  }, [selectedSchedule]);

  const isScheduleEditable = React.useMemo((): boolean => {
    return (
      selectedSchedule && selectedSchedule.status !== ScheduleStatus.Completed
    );
  }, [selectedSchedule]);

  const isScheduleLive = React.useMemo((): boolean => {
    if (!selectedSchedule?.date) return false;
    const selectedScheduleDay = getDateTimeMoment({
      date: selectedSchedule.date,
      timeZone,
    });
    const todayAtTheJobsite = getJobsiteMomentToday({ timeZone });
    return selectedScheduleDay.isSameOrBefore(todayAtTheJobsite, 'day');
  }, [selectedSchedule]);

  React.useEffect(() => {
    onTabApiChange({ refetchData: refetch });
  }, [onTabApiChange, refetch]);

  const value = React.useMemo(
    (): JobsiteSchedulesContextType => ({
      canChangeSchedule,
      completeSchedule,
      date,
      editSchedule,
      gotoNextDay,
      gotoPrevDay,
      handleDateChange,
      isScheduleEditable,
      isScheduleLive,
      jobsiteId,
      loading,
      selectedSchedule,
    }),
    [
      completeSchedule,
      date,
      editSchedule,
      gotoNextDay,
      gotoPrevDay,
      handleDateChange,
      isScheduleEditable,
      isScheduleLive,
      jobsiteId,
      loading,
      selectedSchedule,
    ],
  );

  return (
    <JobsiteSchedulesContext.Provider value={value}>
      {children}
    </JobsiteSchedulesContext.Provider>
  );
}
